<template>
  <div class="vmd-review">
    <!-- 初始化加载遮罩 -->
    <div v-if="initializing" class="init-loading">
      <div class="init-spinner">
        <i class="el-icon-loading" />
        <p>正在加载...</p>
      </div>
    </div>

    <template v-if="!initializing">
    <!-- ==================== 顶部搜索栏 ==================== -->
    <div class="search-panel">
      <div class="search-title" :style="{ marginBottom: searchPanelExpanded ? '' : '0' }">
        图片点评
        <span class="search-toggle" :title="searchPanelExpanded ? '隐藏查询条件' : '展开查询条件'" @click="searchPanelExpanded = !searchPanelExpanded">
          <i :class="searchPanelExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
        </span>
      </div>
      <div v-show="searchPanelExpanded" class="search-form">
        <el-select
          v-model="searchForm.branchCompany"
          placeholder="分公司"
          clearable
          filterable
          size="small"
          class="search-item search-dept"
          @change="onBranchCompanyChange"
        >
          <el-option
            v-for="d in branchCompanyList"
            :key="d"
            :label="d"
            :value="d"
          />
        </el-select>

        <el-select
          v-model="searchForm.region"
          placeholder="区域"
          clearable
          filterable
          size="small"
          class="search-item search-dept"
          @change="onRegionChange"
        >
          <el-option
            v-for="d in regionList"
            :key="d"
            :label="d"
            :value="d"
          />
        </el-select>

        <el-select
          v-model="searchForm.dept"
          placeholder="选择部门"
          clearable
          filterable
          size="small"
          class="search-item search-dept"
          @change="handleSearch"
        >
          <el-option
            v-for="d in deptList"
            :key="d"
            :label="d"
            :value="d"
          />
        </el-select>

        <!-- PC端：日期范围选择器 -->
        <el-date-picker
          v-if="!isMobile"
          v-model="searchForm.dateRange"
          type="daterange"
          range-separator="~"
          start-placeholder="开始"
          end-placeholder="结束"
          size="small"
          value-format="yyyy-MM-dd"
          class="search-item search-date"
          @change="handleSearch"
        />
        <!-- 手机端：开始/结束日期分开选择 -->
        <div v-else class="mobile-date-group">
          <el-date-picker
            v-model="startDate"
            type="date"
            placeholder="开始日期"
            size="small"
            value-format="yyyy-MM-dd"
            class="mobile-date-item"
            @change="onMobileDateChange"
          />
          <el-date-picker
            v-model="endDate"
            type="date"
            placeholder="结束日期"
            size="small"
            value-format="yyyy-MM-dd"
            class="mobile-date-item"
            @change="onMobileDateChange"
          />
        </div>

        <el-input
          v-model="searchForm.userCode"
          placeholder="工号"
          clearable
          size="small"
          class="search-item search-user"
          :class="{ 'auto-filled': userCodeAutoFilled }"
          @keyup.enter.native="handleSearch"
          @clear="onClearUserCode"
          @input="onUserCodeInput"
        />

        <el-input
          v-model="searchForm.userName"
          placeholder="姓名"
          clearable
          size="small"
          class="search-item search-user"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch"
        />

        <el-input
          v-model="searchForm.userPhone"
          placeholder="手机号"
          clearable
          size="small"
          class="search-item search-user"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch"
        />

        <el-input
          v-model="searchForm.keyword"
          placeholder="搜索图片名称…"
          clearable
          size="small"
          class="search-item search-keyword"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch"
        >
          <i slot="prefix" class="el-icon-search" />
        </el-input>

        <div class="search-actions">
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">
            搜索
          </el-button>
          <el-button size="small" @click="handleReset">重置</el-button>
        </div>
      </div>
    </div>

    <!-- ==================== 图片网格 ==================== -->
    <div v-loading="loading" class="image-grid-wrap">
      <div v-if="imageList.length === 0 && !loading" class="empty-state">
        <i class="el-icon-picture-outline" />
        <p>暂无图片数据，请先上传图片</p>
        <el-button type="text" @click="$router.push('/vmd/upload')">前往上传图片</el-button>
      </div>

      <div v-else class="image-grid">
        <div
          v-for="(img, i) in imageList"
          :key="img.id"
          class="image-card"
          @click="openViewer(i)"
        >
          <div class="image-thumb">
            <el-image
              :src="getFullUrl(img.imageUrl)"
              fit="cover"
              lazy
              :preview-src-list="[]"
            >
              <div slot="error" class="image-error">
                <i class="el-icon-picture-outline" />
              </div>
            </el-image>
            <div class="image-overlay">
              <span>{{ commentCounts[img.id] || 0 }} 条点评</span>
            </div>
          </div>
          <div class="image-info">
            <div class="image-name" :title="img.storedName">{{ img.storedName }}</div>
            <div class="image-meta">
              <span><i class="el-icon-office-building" /> {{ img.deptName || '--' }}</span>
              <span>{{ formatDate(img.uploadTime) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div v-if="total > 0" class="pagination-wrap">
      <el-pagination
        background
        layout="prev, pager, next, total"
        :total="total"
        :page-size="searchForm.size"
        :current-page.sync="searchForm.page"
        :pager-count="searchForm.size <= 12 ? 4 : 7"
        @current-change="handlePageChange"
      />
    </div>

    <!-- ==================== 图片查看器弹窗 ==================== -->
    <el-dialog
      :visible.sync="viewerVisible"
      :title="currentImage ? currentImage.storedName : '图片点评'"
      width="98%"
      top="2vh"
      custom-class="viewer-dialog"
      :close-on-click-modal="false"
      :modal="false"
      :before-close="handleCloseViewer"
      destroy-on-close
      @opened="onViewerOpened"
      @closed="onViewerClosed"
    >
      <div class="viewer-container" :class="{ 'comments-open': showCommentArea, 'is-mobile': isMobile }" v-if="currentImage">
        <!-- 左侧图片区 -->
        <div class="viewer-image-section" @click="handleImageClick">
          <div class="viewer-nav">
            <button class="nav-btn nav-prev" @click.stop="prevImage" :disabled="viewerIndex === 0">
              <i class="el-icon-arrow-left" />
            </button>
            <button class="nav-btn nav-next" @click.stop="nextImage" :disabled="viewerIndex >= imageList.length - 1">
              <i class="el-icon-arrow-right" />
            </button>
          </div>

          <div class="image-counter">{{ viewerIndex + 1 }} / {{ imageList.length }}</div>

          <div class="image-container" ref="imageContainer">
            <img
              :src="getFullUrl(currentImage.imageUrl)"
              :alt="currentImage.storedName"
              class="viewer-image"
              ref="viewerImage"
              :style="imageStyle"
              @load="onImageLoaded"
            />

            <!-- 图片上的点评标记点 -->
            <div
              v-for="(marker, mi) in locationMarkers"
              :key="mi"
              class="comment-marker"
              :style="{ left: marker.x + '%', top: marker.y + '%' }"
              :title="marker.content"
              @click.stop="scrollToComment(marker.commentId)"
            >
              {{ mi + 1 }}
            </div>
          </div>

          <!-- 图片缩放旋转控制条 -->
          <div class="image-controls">
            <button class="ctrl-btn" title="放大" @click.stop="zoomIn">
              <i class="el-icon-zoom-in" />
            </button>
            <button class="ctrl-btn" title="缩小" @click.stop="zoomOut">
              <i class="el-icon-zoom-out" />
            </button>
            <button class="ctrl-btn" title="全屏" @click.stop="toggleFullscreen">
              <i :class="isFullscreen ? 'el-icon-full-screen' : 'el-icon-full-screen'" />
            </button>
            <button class="ctrl-btn" title="向左旋转" @click.stop="rotateLeft">
              <i class="el-icon-refresh-left" />
            </button>
            <button class="ctrl-btn" title="向右旋转" @click.stop="rotateRight">
              <i class="el-icon-refresh-right" />
            </button>
          </div>

          <div class="image-actions">
            <button class="ctrl-btn" title="下载" @click.stop="downloadImage">
              <i class="el-icon-download" />
            </button>
            <span class="image-path" :title="currentImage.imagePath">
              {{ currentImage.deptName }} / {{ formatDate(currentImage.uploadTime) }}
            </span>
          </div>

          <!-- 浮动点评入口按钮 -->
          <button
            v-if="!showCommentArea"
            class="comment-trigger-btn"
            @click.stop="showCommentArea = true"
          >
            <i class="el-icon-chat-dot-round" />
            <span>{{ comments.length }}</span>
          </button>
        </div>

        <!-- 右侧/底部点评区（带过渡动画） -->
        <transition name="comment-slide">
          <div class="viewer-comments-section" v-if="showCommentArea">
            <div class="comments-header">
              <span class="comments-title">点评 ({{ comments.length }})</span>
              <el-radio-group v-model="commentFilter" size="mini" @change="filterComments">
                <el-radio-button label="all">全部</el-radio-button>
                <el-radio-button label="text">文字</el-radio-button>
                <el-radio-button label="quick">表情</el-radio-button>
              </el-radio-group>
              <span class="comment-area-toggle" title="收起点评区" @click="showCommentArea = false">
                <i :class="isMobile ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" />
              </span>
            </div>

            <!-- 快速表情栏 -->
            <div class="quick-emoji-bar">
              <span
                v-for="emoji in quickEmojis"
                :key="emoji"
                class="emoji-btn"
                @click="quickReaction(emoji)"
              >{{ emoji }}</span>
            </div>

            <!-- 点评列表 -->
            <div class="comments-body" ref="commentsBody">
              <div v-if="filteredComments.length === 0" class="no-comments">
                <p>暂无点评，点击图片添加定位点评</p>
              </div>
              <div
                v-for="(c, ci) in filteredComments"
                :key="c.id"
                :ref="'comment-' + c.id"
                class="comment-card"
                :class="{ 'comment-highlight': highlightId === c.id }"
              >
                <div class="comment-avatar">{{ c.author ? c.author.charAt(0) : '👤' }}</div>
                <div class="comment-body">
                  <div class="comment-header-row">
                    <span class="comment-author">{{ c.author || '匿名' }}</span>
                    <span v-if="c.locationX != null" class="comment-location-tag">
                      <i class="el-icon-location-outline" /> 图片定位
                    </span>
                    <span class="comment-time">{{ formatDateTime(c.createTime) }}</span>
                  </div>

                  <!-- 文字点评 -->
                  <div v-if="c.commentType !== 'quick'" class="comment-content">
                    {{ c.content }}
                  </div>

                  <!-- 快捷表情 -->
                  <div v-else class="comment-quick">
                    <span class="quick-emoji-text">{{ c.content }}</span>
                  </div>

                  <div class="comment-actions">
                    <span class="action-btn" @click="handleLike(ci)">
                      <i :class="likedSet[c.id] ? 'el-icon-star-on' : 'el-icon-star-off'" />
                      {{ c.likes || 0 }}
                    </span>
                    <span class="action-btn" @click="handleDeleteComment(ci)">删除</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 点评输入区 -->
            <div class="comment-input-area">
              <div v-if="selectedLocation" class="location-indicator">
                <i class="el-icon-location" />
                已定位图片位置 ({{ selectedLocation.x }}%, {{ selectedLocation.y }}%)
                <span class="location-clear" @click="selectedLocation = null">×</span>
              </div>
              <div class="comment-input-row">
                <el-input
                  v-model="commentText"
                  placeholder="输入点评内容，按Enter发送…"
                  size="small"
                  @keyup.enter.native="submitComment"
                />
                <el-button
                  type="primary"
                  size="small"
                  :disabled="!commentText.trim()"
                  @click="submitComment"
                >
                  发送
                </el-button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </el-dialog>
    </template>
  </div>
</template>

<script>
import {
  searchImages,
  getDeptList,
  getBranchCompanyList,
  getRegionList,
  getComments,
  addComment,
  deleteComment,
  likeComment,
  resolveWxUser
} from '@/api/web/vmd'
import { getToken, parseToken } from '@/utils/auth'

export default {
  name: 'VmdReview',

  data() {
    return {
      // 搜索
      searchForm: {
        branchCompany: '',
        region: '',
        dept: '',
        dateRange: [],
        userCode: '',
        userName: '',
        userPhone: '',
        keyword: '',
        page: 1,
        size: 20
      },
      branchCompanyList: [],
      regionList: [],
      deptList: [],
      imageList: [],
      total: 0,
      loading: false,

      // 查看器
      viewerVisible: false,
      viewerIndex: 0,
      currentImage: null,
      comments: [],
      commentFilter: 'all',
      commentText: '',
      selectedLocation: null,  // { x, y }
      highlightId: null,
      likedSet: {},  // { commentId: true }

      // 图片加载
      imageLoaded: false,

      // 图片缩放与旋转
      imageScale: 1,
      imageRotation: 0,
      isFullscreen: false,

      // 当前点评人
      currentUser: { name: '', code: '', phone: '' },
      userCodeAutoFilled: false,
      showCommentArea: false, // 默认隐藏点评区，图片全屏显示
      searchPanelExpanded: true,
      initializing: true,

      // 快速表情
      quickEmojis: ['👍', '❤️', '😊', '🎉', '🔥', '👏', '💪', '✨'],

      // 评论数缓存
      commentCounts: {},

      // 手机端检测
      isMobile: false,
      startDate: '',
      endDate: ''
    }
  },

  computed: {
    filteredComments() {
      if (this.commentFilter === 'all') return this.comments
      return this.comments.filter(c => c.commentType === this.commentFilter)
    },

    locationMarkers() {
      return this.comments
        .filter(c => c.locationX != null && c.locationY != null)
        .map(c => ({
          x: c.locationX,
          y: c.locationY,
          content: c.content || c.commentType,
          commentId: c.id
        }))
    },

    // 图片动态样式
    imageStyle() {
      return {
        transform: `scale(${this.imageScale}) rotate(${this.imageRotation}deg)`
      }
    }
  },

  created() {
    // 手机端检测
    this.isMobile = window.innerWidth <= 600
    // 手机端每页显示12张图片
    if (this.isMobile) {
      this.searchForm.size = 12
    }
    this.fetchBranchCompanyList()
    this.fetchRegionList()     // 初始加载全部区域
    this.fetchDeptList()       // 初始加载全部部门
    this.fetchImages()
    // 企业微信自动识别：检测URL中的code参数，自动解析当前用户
    this.autoDetectWxUser()
  },

  mounted() {
    // 键盘导航
    document.addEventListener('keydown', this.handleKeyDown)
    // 全屏变化监听
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
  },

  methods: {
    // ========== 搜索 ==========
    async fetchBranchCompanyList() {
      try {
        const res = await getBranchCompanyList()
        if (res.success) {
          this.branchCompanyList = res.data || []
        }
      } catch (e) {
        console.error('获取分公司列表失败', e)
      }
    },

    async fetchRegionList(branchCompany) {
      try {
        const res = await getRegionList(branchCompany || undefined)
        if (res.success) {
          this.regionList = res.data || []
        }
      } catch (e) {
        console.error('获取区域列表失败', e)
      }
    },

    async fetchDeptList(region) {
      try {
        const res = await getDeptList(region || undefined)
        if (res.success) {
          this.deptList = res.data || []
        }
      } catch (e) {
        console.error('获取部门列表失败', e)
      }
    },

    async fetchImages() {
      this.loading = true
      try {
        const params = {
          page: this.searchForm.page,
          size: this.searchForm.size
        }
        if (this.searchForm.branchCompany) params.branchCompany = this.searchForm.branchCompany
        if (this.searchForm.region) params.region = this.searchForm.region
        if (this.searchForm.dept) params.dept = this.searchForm.dept
        if (this.searchForm.keyword) params.keyword = this.searchForm.keyword
        if (this.searchForm.userCode) params.userCode = this.searchForm.userCode
        if (this.searchForm.userName) params.userName = this.searchForm.userName
        if (this.searchForm.userPhone) params.userPhone = this.searchForm.userPhone
        if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
          params.startDate = this.searchForm.dateRange[0]
          params.endDate = this.searchForm.dateRange[1]
        }
        const res = await searchImages(params)
        this.imageList = res.records || []
        this.total = res.total || 0

        // 加载评论计数
        this.loadCommentCounts()
      } catch (e) {
        console.error('搜索图片失败', e)
        this.$message.error('搜索失败')
      } finally {
        this.loading = false
      }
    },

    async loadCommentCounts() {
      for (const img of this.imageList) {
        try {
          const res = await getComments(img.id)
          if (res.success) {
            this.$set(this.commentCounts, img.id, (res.data || []).length)
          }
        } catch (e) { /* ignore */ }
      }
    },

    handleSearch() {
      this.searchForm.page = 1
      this.fetchImages()
    },

    onClearUserCode() {
      this.userCodeAutoFilled = false
      this.handleSearch()
    },

    onUserCodeInput() {
      if (this.userCodeAutoFilled) {
        this.userCodeAutoFilled = false
      }
    },

    // ========== 级联筛选 ==========
    onBranchCompanyChange(val) {
      // 清空区域和部门选中值
      this.searchForm.region = ''
      this.searchForm.dept = ''
      // 根据分公司筛选区域：有值则只显示该分公司下的区域，无值则显示全部
      this.fetchRegionList(val || undefined)
      // 区域变了，部门也要重置为全部
      this.fetchDeptList()
      // 触发搜索
      this.handleSearch()
    },

    onRegionChange(val) {
      // 清空部门选中值
      this.searchForm.dept = ''
      // 根据区域筛选部门：有值则只显示该区域下的部门，无值则显示全部
      this.fetchDeptList(val || undefined)
      // 触发搜索
      this.handleSearch()
    },

    handleReset() {
      const mobileSize = this.isMobile ? 12 : 20
      this.searchForm = { branchCompany: '', region: '', dept: '', dateRange: [], userCode: '', userName: '', userPhone: '', keyword: '', page: 1, size: mobileSize }
      this.startDate = ''
      this.endDate = ''
      // 重置筛选后恢复全部区域和部门列表
      this.fetchRegionList()
      this.fetchDeptList()
      this.fetchImages()
    },

    // 手机端：独立日期选择变化时同步到dateRange
    onMobileDateChange() {
      if (this.startDate && this.endDate) {
        // 确保开始日期不晚于结束日期
        if (this.startDate > this.endDate) {
          const temp = this.startDate
          this.startDate = this.endDate
          this.endDate = temp
        }
        this.searchForm.dateRange = [this.startDate, this.endDate]
      } else if (!this.startDate && !this.endDate) {
        this.searchForm.dateRange = []
      } else {
        this.searchForm.dateRange = [this.startDate || '', this.endDate || '']
      }
      this.handleSearch()
    },

    handlePageChange(page) {
      this.searchForm.page = page
      this.fetchImages()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    // ========== 图片查看器 ==========
    openViewer(index) {
      this.viewerIndex = index
      this.currentImage = this.imageList[index]
      this.showCommentArea = false
      this.viewerVisible = true
    },

    onViewerOpened() {
      this.commentText = ''
      this.selectedLocation = null
      this.commentFilter = 'all'
      this.loadComments()
    },

    handleCloseViewer(done) {
      if (this.showCommentArea) {
        this.showCommentArea = false
        return
      }
      done()
    },

    onViewerClosed() {
      this.comments = []
      this.selectedLocation = null
      this.commentText = ''
    },

    async loadComments() {
      if (!this.currentImage) return
      try {
        const res = await getComments(this.currentImage.id)
        if (res.success) {
          this.comments = res.data || []
          this.$set(this.commentCounts, this.currentImage.id, this.comments.length)
        }
      } catch (e) {
        console.error('加载点评失败', e)
      }
    },

    prevImage() {
      if (this.viewerIndex > 0) {
        this.viewerIndex--
        this.currentImage = this.imageList[this.viewerIndex]
        this.loadComments()
        this.selectedLocation = null
        this.commentText = ''
        this.resetTransform()
      }
    },

    nextImage() {
      if (this.viewerIndex < this.imageList.length - 1) {
        this.viewerIndex++
        this.currentImage = this.imageList[this.viewerIndex]
        this.loadComments()
        this.selectedLocation = null
        this.commentText = ''
        this.resetTransform()
      }
    },

    // 图片点击定位（支持缩放旋转后的坐标计算）
    handleImageClick(e) {
      const img = this.$refs.viewerImage
      if (!img) return

      const rect = img.getBoundingClientRect()
      let x = ((e.clientX - rect.left) / rect.width)
      let y = ((e.clientY - rect.top) / rect.height)

      // 根据旋转角度校正坐标（CSS transform 旋转后视觉坐标与实际坐标不一致）
      const r = ((this.imageRotation % 360) + 360) % 360
      let cx, cy
      switch (r) {
        case 90:
          cx = y
          cy = 1 - x
          break
        case 180:
          cx = 1 - x
          cy = 1 - y
          break
        case 270:
          cx = 1 - y
          cy = x
          break
        default:
          cx = x
          cy = y
      }
      this.selectedLocation = {
        x: parseFloat((cx * 100).toFixed(1)),
        y: parseFloat((cy * 100).toFixed(1))
      }
    },

    // ========== 图片缩放与旋转 ==========
    zoomIn() {
      this.imageScale = Math.min(this.imageScale + 0.25, 3)
    },
    zoomOut() {
      this.imageScale = Math.max(this.imageScale - 0.25, 0.25)
    },
    resetTransform() {
      this.imageScale = 1
      this.imageRotation = 0
    },
    rotateLeft() {
      this.imageRotation -= 90
    },
    rotateRight() {
      this.imageRotation += 90
    },
    toggleFullscreen() {
      const el = this.$refs.imageContainer
      if (!document.fullscreenElement) {
        el.requestFullscreen().catch(() => {})
        this.isFullscreen = true
      } else {
        document.exitFullscreen().catch(() => {})
        this.isFullscreen = false
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
    },

    // ========== 当前点评人 ==========

    /**
     * 企业微信自动识别用户身份
     *
     * 完整OAuth2流程：
     *   1. 页面加载时检测code（来自App.vue的OAuth2回调处理）
     *   2. 无code且在企业微信浏览器中 → 主动跳转OAuth2授权
     *   3. OAuth2回调 → code存入sessionStorage → App.vue恢复hash → 本页面再次加载
     *   4. 读取code → 调resolveWxUser解析身份 → 自动收起点评人行
     */
    async autoDetectWxUser() {
      console.log('[VMD-OAuth] ========== autoDetectWxUser 开始 ==========')
      console.log('[VMD-OAuth] location.href:', window.location.href)

      // ========== Step 0: 优先检查后台管理员登录 ==========
      // 如果用户通过admin后台页面登录（存在pepcoAccessToken），
      // 则点评人取后台登录用户名，否则走企微OAuth自动解析。
      const adminToken = getToken()
      if (adminToken) {
        try {
          const adminUsername = parseToken()
          console.log('[VMD-OAuth] Step0: 检测到后台管理员登录, 点评人:', adminUsername)
          this.currentUser = {
            name: adminUsername || '',
            code: '',
            phone: ''
          }
          this.initializing = false
          return
        } catch (e) {
          console.log('[VMD-OAuth] Step0: token解析失败, 回退到企微OAuth:', e.message || e)
        }
      }
      console.log('[VMD-OAuth] Step0: 无后台管理员token, 走企微OAuth流程')
      console.log('[VMD-OAuth] location.search:', window.location.search)
      console.log('[VMD-OAuth] location.hash:', window.location.hash)
      console.log('[VMD-OAuth] sessionStorage vmd_oauth_code:', sessionStorage.getItem('vmd_oauth_code'))
      console.log('[VMD-OAuth] sessionStorage vmd_oauth_hash:', sessionStorage.getItem('vmd_oauth_hash'))
      console.log('[VMD-OAuth] sessionStorage vmd_oauth_attempted:', sessionStorage.getItem('vmd_oauth_attempted'))

      // Step 1: 优先从sessionStorage读取code（App.vue OAuth2回调处理时存入）
      let wxCode = sessionStorage.getItem('vmd_oauth_code')
      if (wxCode) {
        console.log('[VMD-OAuth] Step1: 从sessionStorage读取到code:', wxCode.substring(0, 10) + '...')
        sessionStorage.removeItem('vmd_oauth_code')
      } else {
        console.log('[VMD-OAuth] Step1: sessionStorage中无code')
      }

      // Step 2: 备选 — 从URL search参数读取（code位于hash之前: ?code=xxx#/vmd/review）
      if (!wxCode) {
        const searchMatch = window.location.search.match(/[?&]code=([^&]+)/)
        if (searchMatch) {
          wxCode = decodeURIComponent(searchMatch[1])
          console.log('[VMD-OAuth] Step2: 从URL search提取到code:', wxCode.substring(0, 10) + '...')
        } else {
          console.log('[VMD-OAuth] Step2: URL search中无code')
        }
      }

      // Step 3: 备选 — 从hash query参数读取（code位于hash中: #/vmd/review?code=xxx）
      if (!wxCode) {
        const hashMatch = window.location.hash.match(/[?&]code=([^&]+)/)
        if (hashMatch) {
          wxCode = decodeURIComponent(hashMatch[1])
          console.log('[VMD-OAuth] Step3: 从URL hash提取到code:', wxCode.substring(0, 10) + '...')
        } else {
          console.log('[VMD-OAuth] Step3: URL hash中无code')
        }
      }

      // Step 4: 有code → 调后端解析用户身份
      if (wxCode) {
        console.log('[VMD-OAuth] Step4: 有code, 调resolveWxUser解析...')
        try {
          const res = await resolveWxUser(wxCode)
          console.log('[VMD-OAuth] Step4: resolveWxUser返回:', JSON.stringify(res))
          if (res && res.isNull && res.name) {
            this.currentUser = {
              name: res.name || '',
              code: res.id || '',
              phone: res.phone || ''
            }
            // 自动填入工号到查询条件
            if (res.id) {
              this.searchForm.userCode = res.id
              this.userCodeAutoFilled = true
              this.handleSearch()
            }
            console.log('[VMD-OAuth] Step4: ✅ 身份识别成功! 用户:', res.name)
          } else {
            console.log('[VMD-OAuth] Step4: ⚠️ resolveWxUser返回无效数据(res.isNull=', res && res.isNull, ', res.name=', res && res.name, ')')
          }
        } catch (e) {
          console.error('[VMD-OAuth] Step4: ❌ resolveWxUser异常:', e)
        }
        this.initializing = false
        return
      }

      // Step 5: 无code → 检测企业微信环境，发起OAuth2静默授权
      console.log('[VMD-OAuth] Step5: 无code, 检测企业微信环境...')
      const ua = navigator.userAgent.toLowerCase()
      console.log('[VMD-OAuth] Step5: userAgent:', navigator.userAgent)
      const isWx = ua.includes('wxwork') || ua.includes('micromessenger')
      console.log('[VMD-OAuth] Step5: isWxWork?', ua.includes('wxwork'), 'isWeChat?', ua.includes('micromessenger'), '→ isWx:', isWx)
      if (!isWx) {
        console.log('[VMD-OAuth] Step5: 非企业微信环境，退出')
        this.initializing = false
        return
      }

      // 防止重复发起OAuth2（OAuth2失败/cancel时不再重复跳转）
      if (sessionStorage.getItem('vmd_oauth_attempted')) {
        console.log('[VMD-OAuth] Step5: 检测到vmd_oauth_attempted标记，已尝试过OAuth2，不再重复跳转')
        sessionStorage.removeItem('vmd_oauth_attempted')
        this.initializing = false
        return
      }

      // 保存当前hash路径（双保险：sessionStorage + state参数）
      const currentHash = window.location.hash || '#/vmd/review'
      const routePath = currentHash.replace(/^#/, '')  // '/vmd/review'
      console.log('[VMD-OAuth] Step5: 当前路由→', routePath, '准备跳转OAuth2...')
      sessionStorage.setItem('vmd_oauth_hash', currentHash)
      sessionStorage.setItem('vmd_oauth_attempted', '1')

      // 构造企业微信OAuth2静默授权URL（snsapi_base，用户完全无感）
      // 关键：将目标路由编码到state参数（格式: routePath__nonce），
      //       微信回调时原样返回，不依赖sessionStorage跨跳转持久化。
      //       注意：state 不额外 encode，避免微信服务器端二次编码导致解析失败。
      const corpid = process.env.VUE_APP_WX_CORPID
      const agentId = process.env.VUE_APP_WX_AGENTID
      const redirectUri = encodeURIComponent(window.location.origin + '/')
      const nonce = Math.random().toString(36).substring(2, 10)
      const statePayload = routePath + '__' + nonce
      const oauthUrl = 'https://open.weixin.qq.com/connect/oauth2/authorize'
        + `?appid=${corpid}`
        + `&redirect_uri=${redirectUri}`
        + '&response_type=code'
        + '&scope=snsapi_base'
        + `&state=${statePayload}`
        + `&agentid=${agentId}`
        + '#wechat_redirect'

      console.log('[VMD-OAuth] Step5: 🚀 跳转OAuth2 (state编码路由:', statePayload, ')')
      window.location.replace(oauthUrl)
    },

    // ========== 点评操作 ==========
    async submitComment() {
      const text = this.commentText.trim()
      if (!text || !this.currentImage) return

      // 校验用户身份：未获取到有效用户信息则禁止点评
      if (!this.currentUser.name) {
        this.$message.error('获取用户失败，不允许点评')
        return
      }

      try {
        const data = {
          imageId: this.currentImage.id,
          imagePath: this.currentImage.imagePath,
          imageUrl: this.currentImage.imageUrl,
          imageName: this.currentImage.storedName,
          content: text,
          author: this.currentUser.name || '匿名',
          authorCode: this.currentUser.code || '',
          commentType: 'text',
          deptName: this.currentImage.deptName
        }

        if (this.selectedLocation) {
          data.locationX = this.selectedLocation.x
          data.locationY = this.selectedLocation.y
        }

        const res = await addComment(data)
        if (res.success) {
          this.commentText = ''
          this.selectedLocation = null
          this.loadComments()
        } else {
          this.$message.error(res.message || '添加失败')
        }
      } catch (e) {
        this.$message.error('添加点评失败')
      }
    },

    quickReaction(emoji) {
      if (!this.currentImage) return
      // 校验用户身份：未获取到有效用户信息则禁止点评
      if (!this.currentUser.name) {
        this.$message.error('获取用户失败，不允许点评')
        return
      }
      addComment({
        imageId: this.currentImage.id,
        imagePath: this.currentImage.imagePath,
        imageUrl: this.currentImage.imageUrl,
        imageName: this.currentImage.storedName,
        content: emoji,
        author: this.currentUser.name || '匿名',
        authorCode: this.currentUser.code || '',
        commentType: 'quick',
        deptName: this.currentImage.deptName
      }).then(res => {
        if (res.success) {
          this.loadComments()
          this.showFloatEmoji(emoji)
        }
      }).catch(() => {
        this.$message.error('操作失败')
      })
    },

    showFloatEmoji(emoji) {
      const el = document.createElement('div')
      el.className = 'float-emoji'
      el.textContent = emoji
      document.querySelector('.viewer-image-section').appendChild(el)
      setTimeout(() => el.remove(), 1200)
    },

    handleLike(ci) {
      const comment = this.filteredComments[ci]
      if (!comment) return
      likeComment(comment.id).then(res => {
        if (res.success) {
          this.$set(this.likedSet, comment.id, true)
          this.loadComments()
        }
      }).catch(() => {})
    },

    handleDeleteComment(ci) {
      const comment = this.filteredComments[ci]
      if (!comment) return
      this.$confirm('确定删除该点评吗？', '提示', {
        type: 'warning',
        confirmButtonText: '确定'
      }).then(() => {
        deleteComment(comment.id).then(res => {
          if (res.success) {
            this.$message.success('已删除')
            this.loadComments()
          }
        })
      }).catch(() => {})
    },

    scrollToComment(commentId) {
      this.highlightId = commentId
      setTimeout(() => {
        const el = this.$refs['comment-' + commentId]
        if (el && el[0]) {
          el[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      setTimeout(() => { this.highlightId = null }, 2000)
    },

    filterComments() {
      // computed 自动处理
    },

    // ========== 工具 ==========
    getFullUrl(relativeUrl) {
      if (!relativeUrl) return ''
      return process.env.VUE_APP_IMAGE_URL + relativeUrl
    },

    formatDate(dateStr) {
      if (!dateStr) return '--'
      const d = new Date(dateStr)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },

    formatDateTime(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      return `${this.formatDate(dateStr)} ${h}:${min}`
    },

    downloadImage() {
      if (!this.currentImage) return
      const a = document.createElement('a')
      a.href = this.getFullUrl(this.currentImage.imageUrl)
      a.download = this.currentImage.storedName || 'image.jpg'
      a.click()
    },

    handleKeyDown(e) {
      if (!this.viewerVisible) return
      if (e.key === 'Escape') {
        if (this.showCommentArea) {
          this.showCommentArea = false
        } else {
          this.viewerVisible = false
        }
        return
      }
      if (e.key === 'ArrowRight') this.nextImage()
      else if (e.key === 'ArrowLeft') this.prevImage()
    },

    onImageLoaded() {
      this.imageLoaded = true
    }
  }
}
</script>

<style lang="scss" scoped>
.vmd-review {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: 40px;
}

// 初始化加载遮罩
.init-loading {
  position: fixed;
  inset: 0;
  background: #f5f7fa;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.init-spinner {
  text-align: center;
  i {
    font-size: 40px;
    color: #409eff;
  }
  p {
    margin-top: 16px;
    font-size: 14px;
    color: #909399;
  }
}

// ==================== 搜索栏 ====================
.search-panel {
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  padding: 24px 32px;

  .search-title {
    font-size: 22px;
    font-weight: 600;
    margin-bottom: 16px;
    letter-spacing: 2px;
    display: flex;
    align-items: center;
    gap: 12px;

    .search-toggle {
      cursor: pointer;
      color: #909399;
      font-size: 16px;
      transition: color .2s;
      padding: 2px 4px;
      &:hover { color: #409eff; }
    }
  }

  .search-form {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .search-item {
    flex-shrink: 0;
  }

  .search-dept {
    width: 160px;
  }

  .search-date {
    width: 230px;
  }

  .search-user {
    width: 100px;
  }

  .search-user.auto-filled {
    ::v-deep .el-input__inner {
      color: #409eff;
      font-weight: 600;
      border-color: #409eff;
      background: #ecf5ff;
    }
  }

  .search-keyword {
    width: 180px;
  }

  .search-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  // 平板
  @media (max-width: 1024px) {
    padding: 20px 24px;

    .search-form {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }

    .search-item {
      width: 100%;
      flex-shrink: 1;
    }

    .search-date {
      width: 100%;
    }

    .search-actions {
      grid-column: 1 / -1;
    }
  }

  // 手机端
  @media (max-width: 600px) {
    padding: 16px 16px;

    .search-title {
      font-size: 18px;
      margin-bottom: 12px;
    }

    .search-form {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .search-item {
      width: 100%;
      flex: 1 1 auto;
    }

    .search-date {
      width: 100%;
    }

    // 手机端日期：开始/结束两个独立选择器
    .mobile-date-group {
      display: flex;
      gap: 8px;
      width: 100%;

      .mobile-date-item {
        flex: 1;
        min-width: 0;  // 防止溢出

        .el-input__inner {
          font-size: 13px;
        }
      }
    }

    .search-user {
      width: 100%;
    }

    .search-keyword {
      width: 100%;
    }

    .search-actions {
      width: 100%;
      justify-content: flex-end;
      gap: 8px;
    }
  }
}

// ==================== 图片网格 ====================
.image-grid-wrap {
  padding: 24px 32px;
  min-height: 300px;
}

.empty-state {
  text-align: center;
  padding: 80px 0;
  color: #909399;

  i {
    font-size: 64px;
    display: block;
    margin-bottom: 16px;
    color: #dcdfe6;
  }

  p {
    font-size: 14px;
    margin-bottom: 12px;
  }
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;

  // 手机端：每页固定显示2张图片
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
}

.image-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
}

.image-thumb {
  position: relative;
  width: 100%;
  padding-top: 75%;
  background: #f0f2f5;
  overflow: hidden;

  .el-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .image-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 6px 10px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
    color: #fff;
    font-size: 12px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover .image-overlay {
    opacity: 1;
  }
}

.image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 40px;
  color: #dcdfe6;
}

.image-info {
  padding: 10px 12px;

  .image-name {
    font-size: 13px;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 6px;
  }

  .image-meta {
    font-size: 11px;
    color: #909399;
    display: flex;
    justify-content: space-between;

    i {
      margin-right: 2px;
    }
  }
}

.pagination-wrap {
  text-align: center;
  padding: 0 32px 24px;

  // 手机端分页适配：防止右侧按钮被遮挡
  @media (max-width: 600px) {
    padding: 0 8px 24px;
    overflow-x: auto;

    .el-pagination {
      white-space: nowrap;

      // 缩小页码间距和尺寸，避免换行遮挡
      li, button {
        font-size: 13px;
        min-width: 28px;
        height: 28px;
        line-height: 28px;
        margin: 0 2px;
      }

      // 总数文字缩小
      span:not(li button):not(.btn-next):not(.btn-prev) {
        font-size: 12px;
        margin-left: 6px;
      }
    }
  }
}

// ==================== 图片查看器 ====================
.viewer-container {
  display: flex;
  height: 92vh;
  gap: 0;
  background: rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: hidden;

  @media (max-width: 1024px) {
    flex-direction: column;
    height: auto;
    max-height: calc(100vh - 110px);
  }

  @media (max-width: 600px) {
    max-height: calc(100vh - 80px);
    background: #1a1a2e;
  }

  // 手机横屏：缩小上下边距，让图片更高
  @media (max-width: 900px) and (orientation: landscape) {
    max-height: calc(100vh - 40px);
  }
}

.viewer-image-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
  position: relative;
  min-width: 0;
  overflow: hidden;
  transition: flex 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);

  @media (max-width: 1024px) {
    min-height: calc(100vh - 110px - 40px);
    max-height: calc(100vh - 110px - 40px);
    flex: 1 1 auto;
    transition: max-height 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
  }

  @media (max-width: 600px) {
    min-height: calc(100vh - 80px - 40px);
    max-height: calc(100vh - 80px - 40px);
    background: #0f0f23;
  }

  // 手机横屏：图片撑满宽度，减少顶部高度占用
  @media (max-width: 900px) and (orientation: landscape) {
    min-height: calc(100vh - 40px);
    max-height: calc(100vh - 40px);
  }
}

// 点评面板打开时，图片区缩小
.viewer-container.comments-open {
  .viewer-image-section {
    flex: 0 0 60%;

    @media (max-width: 1024px) {
      flex: 0 0 auto;
      max-height: 45vh;
      min-height: 35vh;
    }

    @media (max-width: 600px) {
      max-height: 40vh;
      min-height: 30vh;
    }
  }
}

.viewer-nav {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 12px;
  pointer-events: none;
  z-index: 10;
}

.nav-btn {
  pointer-events: auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
}

.image-counter {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  padding: 4px 14px;
  border-radius: 12px;
  font-size: 13px;
  z-index: 10;
}

.image-container {
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.viewer-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  cursor: crosshair;
  border-radius: 4px;
  transition: transform 0.2s ease;
}

// 图片标记点
.comment-marker {
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e74c3c;
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 5;
  box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.3);
  transition: transform 0.2s;
  animation: markerPulse 2s infinite;

  &:hover {
    transform: translate(-50%, -50%) scale(1.3);
    z-index: 6;
  }
}

@keyframes markerPulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.3); }
  50% { box-shadow: 0 0 0 8px rgba(231, 76, 60, 0.1); }
}

// 通用 ctrl-btn 样式（缩放/旋转/下载共用）
.ctrl-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &:active {
    background: rgba(255, 255, 255, 0.35);
  }
}

// 图片缩放旋转控制条
.image-controls {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 20px;
  z-index: 10;

  @media (max-width: 600px) {
    bottom: 30px;
    padding: 4px 10px;
    gap: 4px;

    .ctrl-btn {
      width: 28px;
      height: 28px;
      font-size: 14px;
    }
  }

  @media (max-width: 900px) and (orientation: landscape) {
    bottom: 8px;
    padding: 3px 8px;
    gap: 3px;

    .ctrl-btn {
      width: 26px;
      height: 26px;
      font-size: 13px;
    }
  }
}

// 下载按钮 + 部门信息行（左下角固定，不与缩放控制条重叠）
.image-actions {
  position: absolute;
  bottom: 10px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 10;

  .image-path {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// ==================== 浮动点评入口按钮（与 ctrl-btn 统一风格） ====================
.comment-trigger-btn {
  position: absolute;
  bottom: 60px;
  right: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: transparent;
  border: none;
  border-radius: 0;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  z-index: 10;
  transition: background 0.2s;

  i {
    font-size: 22px;
  }

  span {
    font-size: 12px;
    line-height: 1;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &:active {
    background: rgba(255, 255, 255, 0.35);
  }

  @media (max-width: 600px) {
    right: 8px;
    bottom: 50px;
    padding: 4px 6px;

    i { font-size: 18px; }
    span { font-size: 10px; }
  }
}

// ==================== 右侧/底部点评区 ====================
.viewer-comments-section {
  width: 380px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-left: 1px solid #e4e7ed;
  flex-shrink: 0;
  overflow: hidden;

  @media (max-width: 1024px) {
    width: 100%;
    max-height: 55vh;
    min-height: auto;
    flex-shrink: 1;
    border-left: none;
    border-top: 1px solid #e4e7ed;
  }

  @media (max-width: 600px) {
    max-height: 60vh;
  }
}

.comments-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #ebeef5;

  .comments-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
  }

  .comment-area-toggle {
    cursor: pointer;
    color: #909399;
    font-size: 16px;
    padding: 2px 6px;
    border-radius: 4px;
    transition: all .2s;
    &:hover {
      color: #409eff;
      background: #ecf5ff;
    }
  }
}

// 快速表情栏
.quick-emoji-bar {
  display: flex;
  padding: 10px 16px;
  gap: 8px;
  border-bottom: 1px solid #ebeef5;

  .emoji-btn {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.2s;

    &:hover {
      background: #ecf5ff;
      transform: scale(1.2);
    }

    &:active {
      transform: scale(0.9);
    }
  }
}

// 点评列表
.comments-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;

  .no-comments {
    text-align: center;
    padding: 40px 20px;
    color: #909399;
    font-size: 13px;
  }
}

.comment-card {
  display: flex;
  padding: 12px 16px;
  gap: 10px;
  transition: background 0.3s;

  &:hover {
    background: #f5f7fa;
  }

  &.comment-highlight {
    background: #ecf5ff;
  }
}

.comment-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.comment-body {
  flex: 1;
  min-width: 0;
}

.comment-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.comment-author {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.comment-location-tag {
  font-size: 11px;
  color: #e6a23c;
  background: #fdf6ec;
  padding: 1px 6px;
  border-radius: 4px;

  i {
    font-size: 11px;
  }
}

.comment-time {
  font-size: 11px;
  color: #c0c4cc;
  margin-left: auto;
}

.comment-content {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  word-break: break-all;
}

.comment-quick {
  .quick-emoji-text {
    font-size: 28px;
    line-height: 1.3;
  }
}

.comment-actions {
  display: flex;
  gap: 16px;
  margin-top: 6px;

  .action-btn {
    font-size: 12px;
    color: #909399;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 3px;
    transition: color 0.2s;

    &:hover {
      color: #409eff;
    }
  }
}

// 点评输入区
.comment-input-area {
  padding: 12px 16px;
  border-top: 1px solid #ebeef5;
  background: #fafafa;

  @media (max-width: 600px) {
    position: sticky;
    bottom: 0;
    z-index: 5;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }
}

.location-indicator {
  font-size: 12px;
  color: #e6a23c;
  background: #fdf6ec;
  padding: 4px 10px;
  border-radius: 4px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;

  .location-clear {
    margin-left: auto;
    cursor: pointer;
    font-size: 16px;
    color: #909399;
  }
}

.comment-input-row {
  display: flex;
  gap: 8px;
}

// ==================== 飘浮表情动画 ====================
.float-emoji {
  position: absolute;
  font-size: 28px;
  pointer-events: none;
  animation: floatUp 1.2s ease-out forwards;
}

@keyframes floatUp {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-80px) scale(1.5);
  }
}

// ==================== 点评面板滑入/滑出过渡 ====================
// 桌面端：从右侧滑入
.comment-slide-enter-active {
  transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow: hidden;
}
.comment-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.55, 0, 0.55, 0.2);
  overflow: hidden;
}
.comment-slide-enter {
  width: 0 !important;
  opacity: 0;
  transform: translateX(20px);
}
.comment-slide-leave-to {
  width: 0 !important;
  opacity: 0;
  transform: translateX(20px);
}

// 移动端：从底部滑入
@media (max-width: 1024px) {
  .comment-slide-enter-active {
    transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
  .comment-slide-leave-active {
    transition: all 0.25s cubic-bezier(0.55, 0, 0.55, 0.2);
  }
  .comment-slide-enter {
    width: 100% !important;
    max-height: 0 !important;
    opacity: 0;
    transform: translateY(40px);
  }
  .comment-slide-leave-to {
    width: 100% !important;
    max-height: 0 !important;
    opacity: 0;
    transform: translateY(40px);
  }
}
</style>

<!-- 全局样式（弹窗） -->
<style lang="scss">
.viewer-dialog {
  border-radius: 12px !important;
  overflow: hidden;
  background: transparent !important;
  box-shadow: none !important;

  .el-dialog__header {
    padding: 14px 20px;
    background: rgba(0, 0, 0, 0.85);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: #fff;

    .el-dialog__title {
      font-size: 15px;
      font-weight: 600;
      color: #fff;
    }
  }

  .el-dialog__body {
    padding: 0;
    background: transparent;
  }

  .el-dialog__close {
    color: rgba(255, 255, 255, 0.7) !important;
    &:hover { color: #fff !important; }
  }

  .el-dialog__close {
    font-size: 20px;
  }

  // 移动端弹窗适配
  @media (max-width: 600px) {
    margin-top: 2vh !important;

    .el-dialog {
      max-height: calc(100vh - 4vh);
      display: flex;
      flex-direction: column;
    }

    .el-dialog__body {
      overflow: auto;
      flex: 1;
      min-height: 0;
    }
  }

  // 手机横屏：弹窗贴近顶部，最大化图片区域
  @media (max-width: 900px) and (orientation: landscape) {
    margin-top: 0.5vh !important;

    .el-dialog__header {
      padding: 8px 14px;
    }

    .el-dialog {
      max-height: 99vh;
    }
  }
}
</style>
